import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WalletTransaction } from '../types';
import { walletApi, connectionsApi } from '../services/api';
import { showToast } from 'shared-ui';

const Wallet: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [walletId, setWalletId] = useState<string | null>(null);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'receive' | 'send' | 'earn' | 'spend'>('all');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setError] = useState<string | null>(null);

  // Send form
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendNote, setSendNote] = useState('');

  // Request form
  const [requestFrom, setRequestFrom] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [requestConnections, setRequestConnections] = useState<{ id: string; userId: string; userName: string }[]>([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Pending credit requests (incoming = others requesting from me, outgoing = my requests)
  const [incomingRequests, setIncomingRequests] = useState<Array<{ id: string; requesterId: string; amount: number; note?: string; createdAt: string; requester?: { firstName?: string; lastName?: string } }>>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<Array<{ id: string; fromUserId: string; amount: number; note?: string; createdAt: string; fromUser?: { firstName?: string; lastName?: string } }>>([]);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadWalletData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [balanceRes, transactionsRes, requestsRes] = await Promise.all([
        walletApi.getBalance(),
        walletApi.getTransactions(1, 50),
        walletApi.getRequests().catch(() => ({ success: false, data: { incoming: [], outgoing: [] } })),
      ]);

      if (balanceRes?.success) {
        const bal = balanceRes.data?.balance ?? balanceRes.data?.walletBalance ?? balanceRes.data?.totalBalance ?? 0;
        setBalance(Number(bal) || 0);
        if (balanceRes.data?.walletId) setWalletId(balanceRes.data.walletId);
        if (balanceRes.data?.walletAddress) setWalletAddress(balanceRes.data.walletAddress);
      }
      if (transactionsRes?.success) {
        const raw = transactionsRes.data ?? transactionsRes.transactions ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const myId = balanceRes?.data?.walletId ?? walletId;
        const mapped: WalletTransaction[] = list.map((t: any) => {
          const isReceiver = myId && t.receiverWalletId === myId;
          const isSender = myId && t.senderWalletId === myId;
          let type: 'receive' | 'send' | 'earn' | 'spend' = 'receive';
          const txType = (t.type || '').toUpperCase();
          if (txType === 'REWARD' || txType === 'TOP_UP') type = 'earn';
          else if (txType === 'PURCHASE' || txType === 'FEE') type = 'spend';
          else if (isReceiver) type = 'receive';
          else if (isSender) type = 'send';
          const fromAddr = t.senderWallet?.walletAddress || t.senderWalletId;
          const toAddr = t.receiverWallet?.walletAddress || t.receiverWalletId;
          const addrLabel = (a: string) => (typeof a === 'string' && a.length > 12 ? `${a.substring(0, 8)}...${a.slice(-6)}` : a || '');
          return {
            id: t.id,
            type,
            amount: Number(t.amount) || 0,
            status: (t.status || 'CONFIRMED').toLowerCase() as 'pending' | 'confirmed' | 'failed',
            createdAt: t.createdAt || new Date().toISOString(),
            description: t.description || (type === 'receive' ? `From ${addrLabel(fromAddr)}` : type === 'send' ? `To ${addrLabel(toAddr)}` : txType),
            fromAddress: fromAddr,
            toAddress: toAddr,
            fromName: isReceiver ? addrLabel(fromAddr) : undefined,
            toName: isSender ? addrLabel(toAddr) : undefined,
          } as WalletTransaction;
        });
        setTransactions(mapped);
      }
      if (requestsRes?.success && requestsRes?.data) {
        setIncomingRequests(requestsRes.data.incoming || []);
        setOutgoingRequests(requestsRes.data.outgoing || []);
      } else {
        setIncomingRequests([]);
        setOutgoingRequests([]);
      }
    } catch (err) {
      console.error('Failed to load wallet data:', err);
      setError('Failed to load wallet data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount only
  }, []);

  // NOTE: Demo data removed - now using live API data
  // If API returns empty, the UI will show empty state
  
  const loadRequestConnections = async () => {
    try {
      const res = await connectionsApi.getConnections(1);
      if (res?.success && Array.isArray(res?.data)) {
        setRequestConnections(res.data.filter((c: any) => c.userId).map((c: any) => ({
          id: c.id,
          userId: c.userId,
          userName: c.userName || c.displayName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown',
        })));
      } else {
        setRequestConnections([]);
      }
    } catch {
      setRequestConnections([]);
    }
  };

  const handleOpenRequestModal = () => {
    setShowRequestModal(true);
    setRequestFrom('');
    setRequestAmount('');
    setRequestNote('');
    setRequestError(null);
    loadRequestConnections();
  };

  const handleCancelRequest = async (requestId: string) => {
    setCancellingId(requestId);
    try {
      const res = await walletApi.cancelRequest(requestId);
      if (res?.success) {
        loadWalletData(true);
      } else {
        showToast(res?.error || 'Failed to cancel', 'error');
      }
    } finally {
      setCancellingId(null);
    }
  };

  const handleRespondToRequest = async (requestId: string, accept: boolean) => {
    setRespondingId(requestId);
    try {
      const res = await walletApi.respondToRequest(requestId, accept);
      if (res?.success) {
        loadWalletData(true);
      } else {
        showToast(res?.error || 'Failed to respond', 'error');
      }
    } catch {
      showToast('Failed to respond', 'error');
    } finally {
      setRespondingId(null);
    }
  };

  const handleRequestCredits = async () => {
    const amount = parseFloat(requestAmount);
    if (!requestFrom || !amount || amount <= 0) {
      setRequestError('Please select a connection and enter a valid amount.');
      return;
    }
    setRequestLoading(true);
    setRequestError(null);
    try {
      const res = await walletApi.requestCredits(requestFrom, amount, requestNote.trim() || undefined);
      if (res?.success) {
        setShowRequestModal(false);
        setRequestFrom('');
        setRequestAmount('');
        setRequestNote('');
        loadWalletData(true);
      } else {
        setRequestError(res?.error || res?.message || 'Request failed. Backend may need CreditRequest model.');
      }
    } catch (err) {
      console.error('Request credits failed:', err);
      setRequestError('Failed to send request. Try again or use Send to transfer credits.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      const response = await walletApi.send(sendTo, parseFloat(sendAmount), sendNote);
      if (response.success) {
        setShowSendModal(false);
        setSendTo('');
        setSendAmount('');
        setSendNote('');
        loadWalletData(true);
      } else {
        showToast(response.error || 'Failed to send', 'error');
      }
    } catch (err) {
      console.error('Send failed:', err);
      showToast('Failed to send credits', 'error');
    }
  };

  const filteredTransactions = filter === 'all' 
    ? transactions 
    : transactions.filter(t => t.type === filter);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'receive': return '↓';
      case 'send': return '↑';
      case 'earn': return '🎁';
      case 'spend': return '🛒';
      case 'convert': return '🔄';
      default: return '•';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'receive':
      case 'earn':
        return '#28a745';
      case 'send':
      case 'spend':
        return '#dc3545';
      default:
        return '#666';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <span style={{ fontSize: '48px' }}>💰</span>
        <p style={{ color: '#666', marginTop: '20px' }}>Loading wallet...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>💰 Mango Wallet</h1>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            Your Mango Credits (cryptocurrency) - earn, spend, send & receive
          </p>
        </div>
        <button type="button" onClick={() => loadWalletData(true)} disabled={refreshing} style={{ padding: '10px 20px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '10px', cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '14px' }}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Balance Card */}
      <div style={{
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
        borderRadius: '20px',
        padding: '35px',
        color: 'white',
        marginBottom: '30px',
        boxShadow: '0 10px 40px rgba(255, 107, 53, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '30px' }}>
          <div>
            <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>Total Balance</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginTop: '10px' }}>
              <span style={{ fontSize: '48px' }}>🥭</span>
              <span style={{ fontSize: '48px', fontWeight: '700' }}>
                {(balance ?? user?.wallet?.mangoCredits ?? 0).toFixed(2)}
              </span>
            </div>
            {user?.wallet?.pendingCredits && user.wallet.pendingCredits > 0 && (
              <p style={{ margin: '15px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                +{user.wallet.pendingCredits.toFixed(2)} pending confirmation
              </p>
            )}
            
            <div style={{ marginTop: '20px' }}>
              <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>Wallet Address</p>
              <code style={{ 
                background: 'rgba(255,255,255,0.15)', 
                padding: '8px 12px', 
                borderRadius: '8px',
                fontSize: '13px',
                display: 'inline-block',
                marginTop: '5px'
              }}>
                {(walletAddress || user?.wallet?.address) ? `${(walletAddress || user?.wallet?.address || '').substring(0, 10)}...${(walletAddress || user?.wallet?.address || '').slice(-8)}` : '—'}
              </code>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '15px' }}>
            <button
              onClick={() => setShowSendModal(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                minWidth: '100px'
              }}
            >
              <span style={{ fontSize: '24px' }}>↑</span>
              Send
            </button>
            <button
              onClick={() => setShowReceiveModal(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                minWidth: '100px'
              }}
            >
              <span style={{ fontSize: '24px' }}>↓</span>
              Receive
            </button>
            <button
              onClick={handleOpenRequestModal}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '15px 25px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                minWidth: '100px'
              }}
            >
              <span style={{ fontSize: '24px' }}>✉️</span>
              Request
            </button>
          </div>
        </div>
      </div>

      {/* Pending requests – incoming (others requesting from me) */}
      {incomingRequests.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>📥 Pending requests</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {incomingRequests.map((r) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>
                    {r.requester?.firstName || r.requester?.lastName
                      ? `${r.requester.firstName || ''} ${r.requester.lastName || ''}`.trim()
                      : 'Unknown'}
                    {' '}wants 🥭 {r.amount.toFixed(2)}
                  </p>
                  {r.note && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>{r.note}</p>}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => handleRespondToRequest(r.id, true)}
                    disabled={respondingId === r.id || balance < r.amount}
                    style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: respondingId === r.id ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    {respondingId === r.id ? '…' : 'Accept'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRespondToRequest(r.id, false)}
                    disabled={respondingId === r.id}
                    style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: respondingId === r.id ? 'not-allowed' : 'pointer' }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outgoing requests (my requests awaiting response) */}
      {outgoingRequests.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>📤 Sent requests</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {outgoingRequests.map((r) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>
                    Awaiting {r.fromUser?.firstName || r.fromUser?.lastName
                      ? `${r.fromUser.firstName || ''} ${r.fromUser.lastName || ''}`.trim()
                      : 'response'}
                    {' '}– 🥭 {r.amount.toFixed(2)}
                  </p>
                  {r.note && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>{r.note}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => handleCancelRequest(r.id)}
                  disabled={cancellingId === r.id}
                  style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: cancellingId === r.id ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                >
                  {cancellingId === r.id ? '…' : 'Cancel'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {[
          { label: 'Total Earned', value: transactions.filter(t => t.type === 'earn').reduce((sum, t) => sum + t.amount, 0), icon: '🎁', color: '#28a745' },
          { label: 'Total Spent', value: transactions.filter(t => t.type === 'spend').reduce((sum, t) => sum + t.amount, 0), icon: '🛒', color: '#dc3545' },
          { label: 'Sent', value: transactions.filter(t => t.type === 'send').reduce((sum, t) => sum + t.amount, 0), icon: '↑', color: '#667eea' },
          { label: 'Received', value: transactions.filter(t => t.type === 'receive').reduce((sum, t) => sum + t.amount, 0), icon: '↓', color: '#17a2b8' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{stat.label}</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '22px', fontWeight: '700', color: stat.color }}>
                  🥭 {stat.value.toFixed(0)}
                </p>
              </div>
              <span style={{ fontSize: '28px' }}>{stat.icon}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '5px',
          padding: '15px 20px',
          borderBottom: '1px solid #eee',
          overflowX: 'auto'
        }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'earn', label: '🎁 Earned' },
            { id: 'spend', label: '🛒 Spent' },
            { id: 'receive', label: '↓ Received' },
            { id: 'send', label: '↑ Sent' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id as any)}
              style={{
                padding: '10px 16px',
                background: filter === tab.id ? '#ff6b35' : '#f0f0f0',
                color: filter === tab.id ? 'white' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: filter === tab.id ? '600' : '400',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Transaction List */}
        <div style={{ padding: '20px' }}>
          {filteredTransactions.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              No transactions found
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {filteredTransactions.map(tx => (
                <div
                  key={tx.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '12px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '12px',
                      background: getTransactionColor(tx.type) + '20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px'
                    }}>
                      {getTransactionIcon(tx.type)}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '14px' }}>
                        {tx.description}
                      </p>
                      {tx.businessName && (
                        <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#666' }}>
                          {tx.businessName}
                        </p>
                      )}
                      {(tx.fromName || tx.toName) && (
                        <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#666' }}>
                          {tx.type === 'receive' ? `From: ${tx.fromName}` : `To: ${tx.toName}`}
                        </p>
                      )}
                      <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#888' }}>
                        {new Date(tx.createdAt).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '700',
                      color: getTransactionColor(tx.type)
                    }}>
                      {['receive', 'earn'].includes(tx.type) ? '+' : '-'}🥭 {tx.amount.toFixed(2)}
                    </p>
                    <span style={{
                      fontSize: '10px',
                      padding: '3px 8px',
                      borderRadius: '8px',
                      background: tx.status === 'confirmed' ? '#28a74520' : tx.status === 'pending' ? '#ffc10720' : '#dc354520',
                      color: tx.status === 'confirmed' ? '#28a745' : tx.status === 'pending' ? '#ffc107' : '#dc3545',
                      fontWeight: '600'
                    }}>
                      {tx.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* How It Works */}
      <div style={{
        marginTop: '30px',
        background: 'white',
        borderRadius: '16px',
        padding: '25px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>🥭 How Mango Credits Work</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          {[
            { icon: '🛒', title: 'Pay at Checkout', desc: 'Use credits to pay at any Mango business' },
            { icon: '🎁', title: 'Earn Rewards', desc: 'Get credits back on every purchase' },
            { icon: '↔️', title: 'Send & Receive', desc: 'Transfer credits to friends instantly' },
            { icon: '🔐', title: 'Blockchain Secured', desc: 'Every transaction is verified on-chain' },
          ].map((item, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '20px' }}>
              <span style={{ fontSize: '36px' }}>{item.icon}</span>
              <h3 style={{ margin: '15px 0 8px 0', fontSize: '15px', color: '#333' }}>{item.title}</h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Send Modal */}
      {showSendModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '16px',
            width: '450px',
            maxWidth: '90vw'
          }}>
            <h2 style={{ margin: '0 0 25px 0', fontSize: '22px', color: '#333' }}>↑ Send Mango Credits</h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Recipient (Mango ID or Wallet Address)
              </label>
              <input
                type="text"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                placeholder="MP-XX-123456 or 0x..."
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '10px',
                  fontSize: '15px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Amount
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ 
                  position: 'absolute', 
                  left: '14px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  fontSize: '18px'
                }}>
                  🥭
                </span>
                <input
                  type="number"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '14px 14px 14px 45px',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}
                />
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                Available: 🥭 {(balance ?? user?.wallet?.mangoCredits ?? 0).toFixed(2)}
              </p>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Note (optional)
              </label>
              <input
                type="text"
                value={sendNote}
                onChange={(e) => setSendNote(e.target.value)}
                placeholder="What's this for?"
                style={{
                  width: '100%',
                  padding: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '10px',
                  fontSize: '15px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowSendModal(false)}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Send 🥭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {showReceiveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '16px',
            width: '400px',
            maxWidth: '90vw',
            textAlign: 'center'
          }}>
            <h2 style={{ margin: '0 0 10px 0', fontSize: '22px', color: '#333' }}>↓ Receive Mango Credits</h2>
            <p style={{ margin: '0 0 25px 0', color: '#666', fontSize: '14px' }}>
              Share your Mango ID or wallet address to receive credits
            </p>

            {/* QR Code placeholder */}
            <div style={{
              width: '180px',
              height: '180px',
              margin: '0 auto 25px',
              background: '#f0f0f0',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px'
            }}>
              📱
            </div>

            <div style={{ marginBottom: '15px' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666' }}>Your Mango ID</p>
              <code style={{
                display: 'block',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                {user?.mangoId}
              </code>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666' }}>Wallet Address</p>
              <code style={{
                display: 'block',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '12px',
                wordBreak: 'break-all'
              }}>
                {walletAddress || user?.wallet?.address || '—'}
              </code>
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(walletAddress || user?.wallet?.address || '');
                showToast('Wallet address copied!', 'success');
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '10px'
              }}
            >
              📋 Copy Address
            </button>

            <button
              onClick={() => setShowReceiveModal(false)}
              style={{
                width: '100%',
                padding: '14px',
                background: '#f0f0f0',
                color: '#333',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Request Modal */}
      {showRequestModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '16px',
            width: '400px',
            maxWidth: '90vw',
            textAlign: 'left'
          }}>
            <h2 style={{ margin: '0 0 10px 0', fontSize: '22px', color: '#333' }}>✉️ Request Mango Credits</h2>
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>
              Ask a connection to send you credits. They'll be notified and can approve or decline.
            </p>
            {requestConnections.length === 0 ? (
              <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', marginBottom: '20px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#666' }}>Add connections first to request credits from them.</p>
                <Link to="/connections" style={{ display: 'inline-block', marginTop: '12px', color: '#667eea', fontWeight: 600 }}>Go to Connections</Link>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>Request from</label>
                  <select value={requestFrom} onChange={(e) => setRequestFrom(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}>
                    <option value="">Select a connection</option>
                    {requestConnections.map((c) => (
                      <option key={c.id} value={c.userId}>{c.userName}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>Amount (MGC)</label>
                  <input type="number" min="0.01" step="0.01" value={requestAmount} onChange={(e) => setRequestAmount(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>Note (optional)</label>
                  <input type="text" value={requestNote} onChange={(e) => setRequestNote(e.target.value)} placeholder="e.g. Reimbursement" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                {requestError && <p style={{ margin: '0 0 12px 0', color: '#dc3545', fontSize: '13px' }}>{requestError}</p>}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={handleRequestCredits} disabled={requestLoading} style={{ flex: 1, padding: '12px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 600, cursor: requestLoading ? 'not-allowed' : 'pointer' }}>{requestLoading ? 'Sending…' : 'Send Request'}</button>
                  <button type="button" onClick={() => setShowRequestModal(false)} style={{ padding: '12px 20px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: '10px', fontSize: '15px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </>
            )}
            {requestConnections.length === 0 && (
              <button type="button" onClick={() => setShowRequestModal(false)} style={{ width: '100%', marginTop: '16px', padding: '14px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: '10px', fontSize: '15px', cursor: 'pointer' }}>Close</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
